import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
	Autocomplete,
	Box,
	Button,
	Card,
	Container,
	Paper,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import {useFormik} from 'formik';
import {useQuery} from 'react-query';
import {useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import PhoneInput from 'src/components/inputs/PhoneInput';
import useSite from 'src/hooks/useSite';
import useUser from 'src/hooks/useUser';
import {nameValidation, phoneValidation} from 'src/schema/authSchema';
import {request, request2} from 'src/utils/AdminApi';
import * as Yup from 'yup';

const validationSchema = Yup.object({
	name: nameValidation,
	city: Yup.string().required('يرجى اختيار المدينة'),
	phone: phoneValidation,
});

export default function AutoRenewalForm({order, handleSubmit}) {
	const provider = 'vanex';
	const navigate = useNavigate();
	const {user} = useUser();
	const site = useSite();
	const lastPayment = site.payments[site.payments.length - 1];
	const {data: bundles = []} = useQuery(['bundles'], () => request(`/bundles`), {
		refetchOnWindowFocus: false,
	});
	const {data: providerCities = [], providerCitiesIsLoading} = useQuery(
		[provider, 'cities'],
		() => request(`/getProviderCities?provider=${provider}`),
		{
			refetchOnWindowFocus: false,
			onSuccess: (d) => {
				if (d.length > 0 && !formik.values.city) {
					formik.setFieldValue('city', d[0]?.id ?? '');
				}
			},
		},
	);

	const formik = useFormik({
		initialValues: getDefaultValues({payment: lastPayment, user}),
		validationSchema,
		onSubmit: async (values) => {
			if (show_districts && !values.district) {
				return toast.error('يرجى اختيار المنطقة');
			}
			const city = providerCities.find((c) => c.id === values.city)?.name;
			const district = providerDistricts.find((d) => d.id === values.district)?.name;

			const res = await request2('/subscriptions/renew', 'POST', {
				cityId: values.city,
				city,
				districtId: show_districts ? values.district : null,
				district: show_districts ? district : null,
				phone: values.phone,
				bundle_id: values.bundle,
				name: values.name,
			});
			if (!res.error) {
				navigate('/thanks', {
					state: {
						data: res,
					},
				});
			} else {
				return toast.error(res.error_message || 'حدث خطأ ما، يرجى المحاولة لاحقاً');
			}
		},
	});

	const {data: providerDistricts = [], isLoading: providerDistrictsIsLoading} = useQuery(
		[provider, 'districts', formik.values.city],
		() => request(`/getProviderDistricts?provider=${provider}&cityId=${formik.values.city}`),
		{
			refetchOnWindowFocus: false,
			enabled: !!formik.values.city,
			onSuccess: (d) => {
				if (d.length > 0 && !formik.values.district) {
					formik.setFieldValue('district', d[0]?.id ?? '');
				}
			},
		},
	);
	const show_districts = providerDistricts.length > 0;

	return (
		<Container maxWidth="md">
			<Card sx={{p: 2, mt: 2}}>
				<Stack
					// sx={{minWidth: {md: 500, sm: '100%'}}}
					spacing={2}
					direction="column"
					justifyContent="space-between"
				>
					{/* Modern Bundle Selection */}
					<Box>
						<Typography variant="body1" sx={{mb: 1, fontWeight: 'bold'}}>
							اختر نوع الاشتراك
						</Typography>
						<Stack mb={2} direction={{xs: 'column', sm: 'row'}} spacing={2}>
							{bundles.map((bundle) => (
								<Paper
									variant="outlined"
									key={bundle.id}
									elevation={formik.values.bundle === bundle.id ? 3 : 1}
									sx={{
										flex: 1,
										p: 2,
										cursor: 'pointer',
										backgroundColor:
											formik.values.bundle === bundle.id ? 'rgba(7, 100, 88, 0.08)' : 'transparent',
										transition: 'all 0.3s ease',
										'&:hover': {
											backgroundColor: 'rgba(7, 100, 88, 0.04)',
											borderColor: '#076458',
											transform: 'translateY(-2px)',
											boxShadow: '0 4px 12px rgba(7, 100, 88, 0.2)',
										},
										borderRadius: 2,
									}}
									onClick={() => formik.setFieldValue('bundle', bundle.id)}
								>
									<Stack direction="row" alignItems="center" justifyContent="space-between">
										<Box>
											<Typography
												variant="h6"
												sx={{
													fontWeight: 'bold',
													color: formik.values.bundle === bundle.id ? '#076458' : 'text.primary',
												}}
											>
												{bundle.name}
											</Typography>
											<Typography
												variant="subtitle1"
												sx={{
													fontWeight: 'bold',
													color: formik.values.bundle === bundle.id ? '#076458' : 'text.secondary',
												}}
											>
												{bundle.price} دينار
											</Typography>
											{bundle.description && (
												<Typography variant="body2" sx={{color: 'text.secondary', mt: 0.5}}>
													{bundle.description}
												</Typography>
											)}
										</Box>
										<Stack direction="row" alignItems="center" spacing={1}>
											{formik.values.bundle === bundle.id ? (
												<CheckCircleIcon
													sx={{
														color: '#076458',
														fontSize: 28,
													}}
												/>
											) : (
												<RadioButtonUncheckedIcon
													sx={{
														color: 'text.disabled',
														fontSize: 28,
													}}
												/>
											)}
										</Stack>
									</Stack>
								</Paper>
							))}
						</Stack>
					</Box>

					{/* Customer Information Section */}
					<Box sx={{mt: 3}}>
						<Typography variant="body1" sx={{fontWeight: 'bold'}}>
							البيانات المطلوبة لتحصيل قيمة الاشتراك
						</Typography>
					</Box>

					<TextField
						fullWidth
						name="name"
						label="الاسم الكامل"
						value={formik.values.name}
						onChange={formik.handleChange}
						error={formik.touched.name && Boolean(formik.errors.name)}
						helperText={formik.touched.name && formik.errors.name}
					/>

					<PhoneInput
						value={formik.values.phone}
						onChange={(e) => formik.setFieldValue('phone', e.target.value)}
						error={formik.touched.phone && Boolean(formik.errors.phone)}
						helperText={formik.touched.phone && formik.errors.phone}
					/>
					<Autocomplete
						loading={providerCitiesIsLoading}
						fullWidth
						size="small"
						value={providerCities.find((c) => c.id === formik.values.city) ?? null}
						onChange={(e, newValue) => {
							const cityId = newValue?.id ?? '';
							formik.setFieldValue('city', cityId);
							formik.setFieldValue('district', ''); // Reset district when city changes
						}}
						options={providerCities}
						getOptionLabel={(option) => option.name?.trim() || ''}
						isOptionEqualToValue={(option, value) => option?.id === value?.id}
						renderInput={(params) => (
							<TextField
								placeholder="اختر المدينة"
								label="المدينة"
								{...params}
								error={formik.touched.city && Boolean(formik.errors.city)}
								helperText={formik.touched.city && formik.errors.city}
							/>
						)}
					/>
					{show_districts && (
						<Autocomplete
							loading={providerDistrictsIsLoading}
							loadingText="جار التحميل..."
							fullWidth
							value={providerDistricts.find((c) => c.id === formik.values.district) ?? null}
							onChange={(e, newValue) => {
								const districtId = newValue?.id ?? '';
								formik.setFieldValue('district', districtId);
							}}
							size="small"
							options={providerDistricts}
							getOptionLabel={(option) => option.name || ''}
							isOptionEqualToValue={(option, value) => option?.id === value?.id}
							renderInput={(params) => (
								<TextField
									placeholder="اختر المنطقة"
									label="المنطقة"
									{...params}
									error={formik.touched.district && Boolean(formik.errors.district)}
									helperText={formik.touched.district && formik.errors.district}
								/>
							)}
							disabled={!formik.values.city} // Disable until city is selected
						/>
					)}
				</Stack>

				{/* {process.env.NODE_ENV === 'development' && (
				<Stack mt={2} spacing={3} direction="row">
					<p>city id: {formik.values.city}</p>
					<p>district id: {formik.values.district}</p>
				</Stack>
			)} */}

				<Button
					fullWidth
					variant="contained"
					onClick={() => {
						formik.handleSubmit();
					}}
					disabled={formik.isSubmitting}
					size="large"
					sx={{
						mt: 8,
						px: 4,
						py: 1.5,
						borderRadius: 2,
						fontSize: '1.1rem',
						fontWeight: 'bold',
						background: 'linear-gradient(45deg, #076458 30%, #4DBDAA 90%)',
						'&:hover': {
							background: 'linear-gradient(45deg, #02393E 30%, #076458 90%)',
							boxShadow: '0 6px 10px 4px rgba(7, 100, 88, .3)',
						},
					}}
				>
					تجديد الاشتراك
				</Button>
			</Card>
		</Container>
	);
}

function getDefaultValues({payment, user}) {
	/* 
	{
		"city": "طرابلس",
		"district": "السراج",
		"phone": "526889353",
		"name": "حاتم احمد",
		"cityId": 1,
		"districtId": 70
	}
	*/
	const last_payment_info = payment?.extra_attributes ?? {};

	let values = {
		bundle: 'monthly',
	};

	if (last_payment_info?.cityId) {
		values.city = last_payment_info.cityId;
	} else {
		values.city = 1;
	}
	if (last_payment_info?.districtId) {
		values.district = last_payment_info.districtId;
	} else {
		values.district = '';
	}

	if (last_payment_info?.name) {
		values.name = last_payment_info.name;
	} else {
		values.name = user.name || '';
	}

	if (last_payment_info?.phone) {
		values.phone = last_payment_info.phone;
	} else {
		values.phone = user.phone || '';
	}

	return values;
}
